(ns app.front.view.public-records.complaints
  (:require ["ol/proj" :as ol.proj]
            [app.domain.group.config.model :as config-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.root :as root]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :refer [for-frag]]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.landing-page :as landing-page]
            [app.front.view.map-common.info-box :as map-common-info-box]
            [app.front.view.map-common.util :as map-common-util]
            [app.front.view.public-records.common :as pr-common]
            [app.front.view.public-records.ui :as prui]
            [app.front.view.workspace.map :as workspace-map]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; ---- Helper functions -------------------------------------------------------

(defn- complaint-base-search-options [data-db]
  (let [{:keys [config complaint-selected-options]} data-db]
    (merge
      {:domain-group-id (-> config :domain-group :group/id)}
      (pr-common/selected-options->search-options complaint-selected-options))))

(defn- secondary-application-selected-permit-types [data-db]
  (some-> data-db
          complaint-base-search-options
          :permit-types))

(defn- secondary-application-permit-types [data-db]
  (let [case-permit-types     (some-> data-db
                                      :config
                                      :permit-types
                                      (pr-common/available-tab-permit-types :public-records/cases))
        selected-permit-types (secondary-application-selected-permit-types data-db)]
    (if selected-permit-types
      (some->> selected-permit-types
               (set/intersection case-permit-types)
               not-empty)
      case-permit-types)))

(defn- secondary-application-search-enabled?
  "Checked both when issuing a search and when its response lands: a filter
   change can disable the stream while the request is in flight."
  [data-db]
  (or (nil? (secondary-application-selected-permit-types data-db))
      (some? (secondary-application-permit-types data-db))))

(defn- secondary-application-search-options [data-db scope]
  (p/assoc-if (-> data-db
                  complaint-base-search-options
                  (pr-common/assoc-search-scope scope)
                  (assoc :limit pr-common/unpaginated-search-limit))
              :permit-types
              (secondary-application-permit-types data-db)))

(defn- permit-type-options [available-filters permit-type-configs]
  (when-some [pts (not-empty (:permit-types available-filters))]
    (let [valid-pts (-> (pr-common/visible-complaint-permit-type-config-map permit-type-configs)
                        keys
                        set)]
      (not-empty (set/intersection pts valid-pts)))))

(defn- complaint-category-options [available-filters permit-type-configs]
  (->> permit-type-configs
       pr-common/visible-complaint-permit-type-config-map
       (pr-common/available-category-options available-filters)))

(defn- visible-filter-configs [{:keys [config]} available-filters]
  (let [permit-type-configs (:permit-types config)]
    [{:attribute          :permit-types
      :visibility-options (permit-type-options available-filters permit-type-configs)}
     {:attribute          :categories
      :visibility-options (complaint-category-options available-filters permit-type-configs)}
     {:attribute          :municipalities
      :visibility-options (:municipalities available-filters)}]))

(defn- search-complaints-command-data [db]
  (let [{:keys [complaint-extent-polygon complaint-selected-view
                complaint-skip complaint-limit
                complaint-sort-condition
                complaint-selected-property]}                    (::pr-common/data db)
        map-view?                                                (= complaint-selected-view :dashboard-search/map-view)
        ;; The selected property owns the scope when there is one; the viewport
        ;; extent only exists in map view, so the list simply gets no extent.
        scope                                                    (pr-common/search-scope
                                                                   complaint-selected-property
                                                                   (when map-view? complaint-extent-polygon))
        ;; The unpaginated limit exists so that every record in the searched area
        ;; reaches the map. It is the scope that bounds it, so tie it to the scope
        ;; rather than to the view — before the first extent arrives there is
        ;; nothing to bound it and the paginated window is the safe answer.
        unpaginated?                                             (boolean (and map-view? scope))]
    (cond-> (complaint-base-search-options (::pr-common/data db))
      unpaginated?             (assoc :limit pr-common/unpaginated-search-limit)
      (not unpaginated?)       (assoc :skip complaint-skip
                                      :limit complaint-limit)
      :always                  (pr-common/assoc-search-scope scope)
      complaint-sort-condition (assoc :sort-condition complaint-sort-condition))))

(def complaint-selection-keys
  {:search-results-key           :complaint-search-results
   :selected-property-key        :complaint-selected-property
   :selected-result-key          :complaint-selected-result
   :selected-cluster-results-key :complaint-selected-cluster-results
   :explicit-map-center-key      :complaint-explicit-map-center})

(def secondary-application-selection-keys
  {:search-results-key           :secondary-application-search-results
   :selected-result-key          :secondary-application-selected-result
   :selected-cluster-results-key :complaint-selected-cluster-results})

;; ---- Events -----------------------------------------------------------------

(rf/reg-event-fx ::search
  (fn [{:keys [db]} [_ {:keys [keep-results? sort-condition from-map-move?]}]]
    (let [data-db (::pr-common/data db)]
      ;; Ignore a debounced map search that lands after leaving map view.
      (when (or (not from-map-move?)
                (= :dashboard-search/map-view (:complaint-selected-view data-db)))
        (let [actual-sort        (or sort-condition (:complaint-sort-condition data-db))
              new-skip           (pr-common/search-skip (:complaint-skip data-db) keep-results?)
              request-generation (inc (or (:complaint-search-generation data-db) 0))
              updated-data-db    (cond-> (assoc data-db
                                                :complaint-skip new-skip
                                                :complaint-search-generation request-generation
                                                :complaint-loading-more? (boolean keep-results?))
                                   actual-sort (assoc :complaint-sort-condition actual-sort))
              updated-db         (assoc db ::pr-common/data updated-data-db)]
          {:db updated-db
           :fx [[:dispatch [:comm/command-monitored
                            {:command    :public-records/search-complaints
                             :data       (search-complaints-command-data updated-db)
                             :on-success [::search.success {:keep-results?      keep-results?
                                                            :from-map-move?     from-map-move?
                                                            :request-generation request-generation}]}]]]})))))

(defn- secondary-search-refresh-fx [{:keys [include-secondary-map-records?
                                            complaint-selected-view
                                            complaint-extent-polygon]}]
  (when (and include-secondary-map-records?
             (= complaint-selected-view :dashboard-search/map-view)
             complaint-extent-polygon)
    [[:dispatch [::search-secondary-applications-for-map]]]))

(rf/reg-event-fx ::search.success
  pr-common/data-path
  (pr-common/make-search-success-handler
    {:total-count-key           :complaint-total-count
     :available-filters-key     :complaint-available-filters
     :selected-options-key      :complaint-selected-options
     :search-results-key        :complaint-search-results
     :visible-filters-key       :complaint-visible-filters
     :visible-filter-configs-fn visible-filter-configs
     :search-evt                ::search
     :secondary-refresh-fx-fn   secondary-search-refresh-fx
     :request-generation-key    :complaint-search-generation}))

(rf/reg-event-fx ::load-more
  pr-common/data-path
  (pr-common/make-load-more-handler
    {:skip-key   :complaint-skip
     :limit-key  :complaint-limit
     :search-evt ::search}))

(rf/reg-event-fx ::set-selected-options
  pr-common/data-path
  (fn [{:keys [db]} [_ attribute options]]
    (let [new-db (-> (assoc-in db [:complaint-selected-options attribute] options)
                     (dissoc :complaint-selected-cluster-results))]
      {:db new-db
       :fx (into [[:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx new-db))})))

(rf/reg-event-fx ::set-selected-view
  pr-common/data-path
  (fn [{:keys [db]} [_ view]]
    (let [new-db    (-> (assoc db :complaint-selected-view view)
                        (dissoc :complaint-selected-cluster-results))
          map-view? (= view :dashboard-search/map-view)]
      {:db new-db
       :fx (cond-> []
             (or (not map-view?) (:complaint-extent-polygon new-db))
             (conj [:dispatch [::search {:keep-results? false}]])

             :always
             (into (secondary-search-refresh-fx new-db)))})))

(rf/reg-event-fx ::map-moved
  pr-common/data-path
  (fn [{:keys [db]} [_ ^js ol-map]]
    (when (= :dashboard-search/map-view (:complaint-selected-view db))
      (let [{:keys [include-secondary-map-records? complaint-selected-property]} db]
        (cond-> {:db (assoc db :complaint-extent-polygon (map-common-util/map-extent->polygon ol-map))}
          (not complaint-selected-property)
          (assoc :dispatch-debounce
                 (cond-> [{:id      ::search-for-map
                           :timeout 300
                           :action  :dispatch
                           :event   [::search {:keep-results?  false
                                               :from-map-move? true}]}]
                   include-secondary-map-records?
                   (conj {:id      ::search-secondary-applications-for-map
                          :timeout 300
                          :action  :dispatch
                          :event   [::search-secondary-applications-for-map]}))))))))

(rf/reg-event-fx ::search-secondary-applications-for-map
  (fn [{:keys [db]} _]
    (let [{:keys [complaint-extent-polygon complaint-selected-property complaint-selected-view
                  include-secondary-map-records?]                                              :as data-db} (::pr-common/data db)]
      (if (and include-secondary-map-records?
               complaint-extent-polygon
               (secondary-application-search-enabled? data-db))
        (when (= complaint-selected-view :dashboard-search/map-view)
          {:fx [[:dispatch [:comm/command-monitored
                            {:command    :public-records/search-applications
                             :data       (secondary-application-search-options
                                           data-db
                                           (pr-common/search-scope
                                             complaint-selected-property
                                             complaint-extent-polygon))
                             :on-success [::search-secondary-applications-for-map.success]}]]]})
        {:db (update db ::pr-common/data dissoc
                     :secondary-application-search-results
                     :secondary-application-total-count
                     :secondary-application-selected-result)}))))

(rf/reg-event-db ::search-secondary-applications-for-map.success
  pr-common/data-path
  (fn [db [_ _ response]]
    (cond-> db
      (and (secondary-application-search-enabled? db)
           (:include-secondary-map-records? db)
           (= (pr-common/tab-idx->tab (:tabs db) (:selected-tab db))
              :public-records/complaints))
      (assoc :secondary-application-search-results (:results response)
             :secondary-application-total-count (:total-count response)))))

(rf/reg-event-fx ::map-click
  (pr-common/make-map-click-handler
    {:single-feature-fx    (fn [db js-feature]
                             (pr-common/select-combined-single-feature-fx db
                                                                          js-feature
                                                                          complaint-selection-keys
                                                                          secondary-application-selection-keys))
     :cluster-results-fx   (fn [db features]
                             (if (get-in db [::pr-common/data :include-secondary-map-records?])
                               (pr-common/select-combined-cluster-results-fx db
                                                                             features
                                                                             complaint-selection-keys
                                                                             secondary-application-selection-keys)
                               (pr-common/select-cluster-results-fx db features complaint-selection-keys)))
     :search-success-evt   ::map-click.success
     :search-not-found-evt ::map-click.not-found
     :layer-id-fn          (fn [db]
                             [(if (get-in db [::pr-common/data :include-secondary-map-records?])
                                pr-common/combined-results-layer-id
                                pr-common/complaint-results-layer-id)])}))

(rf/reg-event-fx ::map-click.success
  pr-common/data-path
  (fn [{:keys [db]} [_ click-coordinates _ feature]]
    (let [selected-property (pr-common/gis-data->selected-property feature)
          search-value      (or (-> feature :address :address/full-address) "")
          new-db            (-> db
                                (assoc :complaint-explicit-map-center click-coordinates
                                       :complaint-selected-property selected-property)
                                (dissoc :complaint-selected-result
                                        :secondary-application-selected-result
                                        :complaint-selected-cluster-results))]
      {:db new-db
       :fx (into [[:dispatch [::gis-search/set-search-value search-value]]
                  [:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx new-db))})))

(rf/reg-event-fx ::map-click.not-found
  pr-common/data-path
  (fn [{:keys [db]} _]
    (let [new-db (dissoc db
                         :complaint-selected-property
                         :selected-property
                         :complaint-selected-result
                         :secondary-application-selected-result
                         :complaint-selected-cluster-results)]
      {:db new-db
       :fx (cond-> [[:dispatch [::gis-search/set-search-value ""]]]
             (:complaint-selected-property db)
             (-> (conj [:dispatch [::search {:keep-results? false}]])
                 (into (secondary-search-refresh-fx new-db))))})))

(rf/reg-event-fx ::select-property
  pr-common/data-path
  (fn [{:keys [db]} [_ feature]]
    (let [new-db (-> (pr-common/select-property-db db feature complaint-selection-keys)
                     (dissoc :secondary-application-selected-result))]
      {:db new-db
       :fx (into [[:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx new-db))})))

(rf/reg-event-db ::set-selected-result
  pr-common/data-path
  (fn [db [_ complaint]]
    (-> db
        (pr-common/set-selected-result-db complaint (:selected-result-key complaint-selection-keys))
        (dissoc :secondary-application-selected-result))))

(rf/reg-event-db ::set-secondary-application-selected-result
  pr-common/data-path
  (fn [db [_ workspace]]
    (-> db
        (assoc :secondary-application-selected-result workspace)
        (dissoc :complaint-selected-result))))

(rf/reg-event-fx ::close-selected-property-view
  pr-common/data-path
  (fn [{:keys [db]} _]
    (let [new-db (-> db
                     (pr-common/close-selected-property-view-db complaint-selection-keys)
                     (dissoc :selected-property
                             :secondary-application-selected-result))]
      {:db new-db
       :fx (cond-> []
             (:complaint-selected-property db)
             (-> (conj [:dispatch [::search {:keep-results? false}]])
                 (into (secondary-search-refresh-fx new-db))))})))

(rf/reg-event-db ::close-cluster-view
  pr-common/data-path
  (fn [db _]
    (pr-common/close-cluster-view-db db complaint-selection-keys)))

(rf/reg-event-db ::close-selected-result-view
  pr-common/data-path
  (fn [db _]
    (-> db
        (pr-common/close-selected-result-db complaint-selection-keys)
        (dissoc :secondary-application-selected-result))))

(rf/reg-event-fx ::clear-selected-property
  (fn [{:keys [db]} _]
    (let [new-db (update db ::pr-common/data
                         #(-> %
                              (pr-common/close-selected-property-view-db complaint-selection-keys)
                              (dissoc :selected-property)))]
      {:db new-db
       :fx (into [[:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx (::pr-common/data new-db)))})))

(rf/reg-event-db ::show-on-map
  pr-common/data-path
  (fn [db [_ complaint]]
    (let [map-center (some-> complaint :location :coordinates clj->js ol.proj/fromLonLat js->clj)]
      (-> db
          (assoc :complaint-selected-view :dashboard-search/map-view
                 :complaint-selected-result complaint
                 :complaint-explicit-map-center map-center)
          (dissoc :complaint-selected-cluster-results)))))

;; ---- Subscriptions ----------------------------------------------------------

(rf/reg-sub ::search-results
  :<- [::pr-common/data]
  :-> :complaint-search-results)

(rf/reg-sub ::total-count
  :<- [::pr-common/data]
  (fn [{:keys [complaint-total-count
               secondary-application-total-count
               include-secondary-map-records?
               complaint-selected-view]}]
    (if (and include-secondary-map-records?
             (= complaint-selected-view :dashboard-search/map-view))
      (+ (or complaint-total-count 0) (or secondary-application-total-count 0))
      complaint-total-count)))

(rf/reg-sub ::selected-options
  :<- [::pr-common/data]
  (fn [data [_ attribute]]
    (-> data :complaint-selected-options attribute set)))

(rf/reg-sub ::visible-filters
  :<- [::pr-common/data]
  (fn [data _]
    (or (:complaint-visible-filters data) #{})))

(rf/reg-sub ::complaint-available-filters
  :<- [::pr-common/data]
  :-> :complaint-available-filters)

(rf/reg-sub ::permit-types
  :<- [::complaint-available-filters]
  :<- [::pr-common/permit-type-configs]
  (fn [[available-filters permit-type-configs]]
    (permit-type-options available-filters permit-type-configs)))

(rf/reg-sub ::categories
  :<- [::complaint-available-filters]
  :<- [::pr-common/permit-type-configs]
  :<- [:language]
  (fn [[available-filters permit-type-configs lang]]
    (some->> (complaint-category-options available-filters permit-type-configs)
             (config-model/sort-group-configs lang))))

(rf/reg-sub ::municipality-options
  :<- [::complaint-available-filters]
  :<- [::pr-common/municipalities]
  (pr-common/make-municipality-options-sub))

(rf/reg-sub ::columns
  :<- [::municipality-options]
  :<- [::permit-types]
  :<- [:tr]
  :<- [:language]
  :<- [::date-util/date-fn]
  :<- [::pr-common/permit-type-names]
  (fn [[municipalities permit-types tr lang format-date permit-type-names] _]
    (let [municipality-number->name (->> municipalities
                                         (map (fn [m]
                                                [(:municipality/number m)
                                                 (municipality-model/municipality-name m lang)]))
                                         (into {}))]
      (into []
            (concat (when (du/at-least-two? municipalities)
                      [[(tr [:public-records/municipality])
                        #(->> % :municipality/number (get municipality-number->name))]])
                    (when (pr-common/show-filter? permit-types)
                      [[(tr [:public-records/permit-type])
                        #(get permit-type-names (pr-common/result-permit-type %))]])
                    [[(tr [:public-records/address])
                      :address/full-address]]
                    [[(tr [:public-records/category])
                      #(-> % :category-names (i18n-model/get-text-from-entities-or-map lang))]]
                    [[(tr [:public-records/severity])
                      #(some-> % :complaint/severity vector tr)]
                     [(tr [:public-records/created])
                      #(some-> % :created format-date)
                      {:sorting            (fn [a b]
                                             (compare (:created a)
                                                      (:created b)))
                       :sort-attribute-key :created
                       :on-sort-hook       (partial pr-common/on-sort-hook ::search :created)}]])))))

(rf/reg-sub ::selected-view
  :<- [::pr-common/data]
  :-> :complaint-selected-view)

(rf/reg-sub ::loading-more?
  :<- [::pr-common/data]
  :-> :complaint-loading-more?)

(rf/reg-sub ::selected-property
  :<- [::pr-common/data]
  :-> :complaint-selected-property)

(rf/reg-sub ::primary-selected-result
  :<- [::pr-common/data]
  :-> :complaint-selected-result)

(rf/reg-sub ::selected-cluster-results
  :<- [::pr-common/data]
  :-> :complaint-selected-cluster-results)

(rf/reg-sub ::selected-cluster-ids
  :<- [::selected-cluster-results]
  (fn [results _]
    (set (map :id results))))

(rf/reg-sub ::secondary-application-selected-result
  :<- [::pr-common/data]
  :-> :secondary-application-selected-result)

(rf/reg-sub ::selected-result
  :<- [::primary-selected-result]
  :<- [::secondary-application-selected-result]
  (fn [[selected-result secondary-application-selected-result] _]
    (or selected-result
        secondary-application-selected-result)))

(rf/reg-sub ::secondary-application-search-results
  :<- [::pr-common/data]
  :-> :secondary-application-search-results)

(rf/reg-sub ::property-results
  :<- [::search-results]
  :<- [::secondary-application-search-results]
  (fn [[complaints applications] _]
    (vec (concat complaints applications))))

(rf/reg-sub ::explicit-map-center
  :<- [::pr-common/data]
  :-> :complaint-explicit-map-center)

(rf/reg-sub ::map-center
  :<- [::explicit-map-center]
  :<- [::selected-options :municipalities]
  :<- [::pr-common/municipalities]
  (fn [[explicit-center selected-municipalities municipalities]]
    (or explicit-center
        (->> (or (not-empty selected-municipalities) municipalities)
             (keep :municipality/center-location)
             municipality-model/center-of-centers))))

(rf/reg-sub ::selected-or-all-municipality-numbers
  :<- [::pr-common/municipalities]
  :<- [::selected-options :municipalities]
  (fn [[municipalities selected-municipalities] _]
    (mapv :municipality/number
          (or (not-empty selected-municipalities)
              municipalities))))

(rf/reg-sub ::search-results-layer
  :<- [::search-results]
  :<- [::primary-selected-result]
  :<- [::selected-cluster-ids]
  :<- [:tr]
  (fn [[search-results selected-result selected-cluster-ids tr] _]
    (->> (pr-common/search-results->feature-collection search-results
                                                       selected-result
                                                       selected-cluster-ids)
         (pr-common/feature-collection->map-layer pr-common/complaint-results-layer-id
                                                  (tr [:public-records/complaints])))))

(rf/reg-sub ::combined-search-results-layer
  :<- [::search-results]
  :<- [::primary-selected-result]
  :<- [::secondary-application-search-results]
  :<- [::secondary-application-selected-result]
  :<- [::selected-cluster-ids]
  :<- [:tr]
  (fn [[primary-results primary-selected-result secondary-results secondary-selected-result selected-cluster-ids tr] _]
    (let [combined-results         (concat primary-results secondary-results)
          combined-selected-id     (:id (or primary-selected-result secondary-selected-result))
          combined-selected-result (when combined-selected-id
                                     {:id combined-selected-id})]
      (->> (pr-common/search-results->feature-collection combined-results
                                                         combined-selected-result
                                                         selected-cluster-ids)
           (pr-common/feature-collection->map-layer pr-common/combined-results-layer-id
                                                    (tr [:public-records/public-records]))))))

(rf/reg-sub ::sort-condition
  :<- [::pr-common/data]
  :-> :complaint-sort-condition)

(rf/reg-sub ::initial-sorting
  :<- [::sort-condition]
  :<- [::columns]
  (fn [[sort-condition columns] _]
    (pr-common/sort-condition->initial-sorting sort-condition columns)))

;; ---- View components --------------------------------------------------------

(defn- search-filter [tr]
  (let [lang              (<sub [:language])
        visible-filters   (<sub [::visible-filters])
        permit-type-names (<sub [::pr-common/permit-type-names])]
    [pr-common/render-search-filters tr visible-filters
     (pr-common/search-filter-configs
       {:id-prefix            "complaint-"
        :selected-options     {:municipalities (<sub [::selected-options :municipalities])
                               :permit-types   (<sub [::selected-options :permit-types])
                               :categories     (<sub [::selected-options :categories])}
        :set-options-fn       #(>evt [::set-selected-options %1 %2])
        :lang                 lang
        :permit-type-names    permit-type-names
        :municipality-options (<sub [::municipality-options])
        :permit-type-options  (<sub [::permit-types])
        :category-options     (<sub [::categories])})]))

(defn- search-result-table [tr]
  [sortable-table/table
   {:id              "complaint-search-results"
    :initial-sorting (<sub [::initial-sorting])
    :cols            (<sub [::columns])
    :configurable?   true
    :id-func         :id
    :items           (<sub [::search-results])
    :on-row-click    #(when (:location %)
                        (>evt [::show-on-map %]))
    :tr              tr
    :sticky-header?  true}])

(defn- list-view [tr in-progress?]
  [pr-common/render-paginated-table
   {:tr            tr
    :in-progress?  in-progress?
    :loading-more? (<sub [::loading-more?])
    :results       (<sub [::search-results])
    :total-count   (<sub [::total-count])
    :table         [search-result-table tr]
    :load-more-evt ::load-more}])

(defn- property-result-item [tr lang {:keys [:complaint/severity category-names :entity/type] :as result}]
  (let [workspace?  (= type :entity.type/workspace)
        permit-type (pr-common/result-permit-type result)]
    [:div.w-full
     [:button.p-3.bg-white.mt-2.w-full.border-0.text-left.select-search-result-button
      {:on-click #(>evt [(if workspace?
                           ::set-secondary-application-selected-result
                           ::set-selected-result)
                         result])}
      (when permit-type
        [:span.font-bold
         (<sub [::pr-common/permit-type-name permit-type])])
      (when category-names
        [:span.text-gray-800 (when permit-type ", ") (pr-common/category-name result lang)])
      (when severity
        [:span.text-gray-800 (when (or permit-type category-names) ", ") (tr [severity])])
      (when workspace?
        [:span.text-gray-800
         (when (or permit-type category-names severity) ", ")
         (tr [:public-records/cases])])]]))

(defn- selected-property-view [selected-property]
  (let [tr                (<sub [:tr])
        lang              (<sub [:language])
        property-id       (-> selected-property :property :property/id)
        results           (<sub [::property-results])
        address           (-> selected-property :address :address/full-address)
        municipality-name (or (<sub [::pr-common/municipality-name-by-number (:municipality selected-property)])
                              (<sub [::pr-common/single-municipality-name]))]
    [map-common-info-box/right-side-box
     {:on-cancel-click #(>evt [::close-selected-property-view])
      :tr              tr}
     [:div.flex-grow.overflow-y-auto.px-2.pb-2
      [:h3.font-bold.text-lg.mb-2
       (or address (tr [:address/unknown-address]))
       (when municipality-name
         (str ", " municipality-name))]
      (when property-id
        [:div.text-sm.text-gray-800.mb-3 property-id])
      (cond
        (or (<sub [:comm/command-processing? :public-records/search-complaints])
            (<sub [:comm/command-processing? :public-records/search-applications]))
        [:div.flex.justify-center.py-4
         (cc/loading-spinner {:with-overlay false :small true})]

        (seq results)
        (for-frag [complaint (sort-by :id results)]
          [property-result-item tr lang complaint])

        :else
        [:div.text-gray-800.py-4
         (tr [:public-records/no-results-found])])]]))

(defn- selected-cluster-view [results]
  (let [tr                (<sub [:tr])
        lang              (<sub [:language])
        selected-property (<sub [::selected-property])]
    [pr-common/cluster-results-view
     {:tr              tr
      :results         results
      ;; × resets the whole right-hand side, ← peels off just this layer — and
      ;; only when there is one underneath to return to. Same split as
      ;; selected-result-view below.
      :on-back-click   (when selected-property
                         #(>evt [::close-cluster-view]))
      :on-cancel-click #(>evt [::close-selected-property-view])
      :render-item     (fn [result] [property-result-item tr lang result])}]))

(defn- selected-result-view [{:keys [:entity/type
                                     :municipality/number
                                     :address/full-address
                                     location]
                              :as   selected-result}]
  (let [permit-type                    (pr-common/result-permit-type selected-result)
        tr                             (<sub [::root/permit-types-tr [permit-type]])
        lang                           (<sub [:language])
        format-date                    (<sub [::date-util/date-fn])
        selected-property              (<sub [::selected-property])
        selected-cluster-results       (<sub [::selected-cluster-results])
        municipality-name              (<sub [::pr-common/municipality-name-by-number number])
        include-secondary-map-records? (:include-secondary-map-records? (<sub [::pr-common/data]))
        fmt-opts                       {:tr tr :lang lang :entity selected-result :format-date-time format-date}
        complaint-attrs                (pr-common/format-detail-attrs fmt-opts
                                                                      [[:category-names     :public-records/category]
                                                                       [:created            :public-records/created]
                                                                       [:domain/state       :complaint/state]
                                                                       [:complaint/severity :complaint/severity]])
        case-attrs                     (pr-common/format-detail-attrs fmt-opts
                                                                      [[:workspace/case-description :reporting/case-description]
                                                                       [:category-names             :public-records/category]
                                                                       [:created                    :public-records/created]])
        attrs                          (if (= type :entity.type/workspace)
                                         case-attrs
                                         complaint-attrs)]
    [map-common-info-box/right-side-box
     {:on-cancel-click #(>evt [::close-selected-property-view])
      ;; × resets the whole right-hand side, ← peels off just this layer — and
      ;; only when there is one underneath to return to. Same split as the
      ;; complaints dashboard map (complaint.dashboard/complaint-view).
      :on-back-click   (when (or selected-cluster-results selected-property)
                         #(>evt [::close-selected-result-view]))
      :tr              tr}
     [:div.flex-grow.overflow-y-auto.px-2.pb-2
      [:h3.font-bold.text-lg.mb-2
       (or full-address
           (pr-common/location-coordinates location)
           (tr [:address/unknown-address]))
       (when municipality-name
         (str ", " municipality-name))]
      (when-some [property-id (-> selected-property :property :property/id)]
        [:div.text-sm.text-gray-800.mb-3 property-id])
      (when permit-type
        [:div.uppercase.text-xs.font-bold.mb-3
         (<sub [::pr-common/permit-type-name permit-type])])
      [:div.flex.flex-col.flex-1.gap-3.justify-start
       (into [:<>]
             (map (fn [[label value]] [prui/form-row label value]))
             (cond-> []
               include-secondary-map-records?
               (conj [(tr [:public-records/record-type])
                      (pr-common/record-type-label tr type)])

               :always
               (into attrs)))]]]))

(defn complaint-search-result-map []
  (r/with-let [*post-render-fired? (r/atom false)]
    (let [map-center                     (<sub [::map-center])
          gis-map-layers                 (<sub [::pr-common/gis-map-layers])
          primary-layers                 (into (vec gis-map-layers) (<sub [::search-results-layer]))
          combined-layers                (into (vec gis-map-layers) (<sub [::combined-search-results-layer]))
          include-secondary-map-records? (:include-secondary-map-records? (<sub [::pr-common/data]))
          domain-group-id                (<sub [::pr-common/domain-group-id])
          selected-property              (<sub [::selected-property])
          selected-result                (<sub [::selected-result])
          selected-cluster-results       (<sub [::selected-cluster-results])
          point-search-loading?          (<sub [::map-common-util/point-search-loading?])
          layers                         (if include-secondary-map-records?
                                           combined-layers
                                           primary-layers)]
      (when map-center
        [:div.flex.items-center.flex-wrap.lg:flex-nowrap
         [:div.flex.map-boundary.map-extended.bg-gray-400.rounded.overflow-hidden.h-full.w-full.mt-6
          {:class (when (or point-search-loading? selected-property selected-result selected-cluster-results)
                    ["lg:w-1-2" "lg:mr-2"])
           :style {:height "500px"}}
          [workspace-map/workspace-map
           {:center             map-center
            :layers             layers
            :extended?          true
            :primaryMapMarkers  []
            :events             [{:eventType     "singleclick"
                                  :eventFunction #(>evt [::map-click
                                                         %
                                                         {:group/id domain-group-id}])}
                                 {:eventType     "moveend"
                                  :eventFunction #(>evt [::map-moved (.-map ^js %)])}
                                 {:eventType     "postrender"
                                  :eventFunction (fn [e]
                                                   ;; Ensure the initial map search runs after first render
                                                   (when-not @*post-render-fired?
                                                     (reset! *post-render-fired? true)
                                                     (>evt [::map-moved (.-map ^js e)])))}]
            :zoom               15
            :propertyGeometries (if-some [geometry (:geometry selected-property)]
                                  {:primaryPropertyGeometry (property-model/geojson-feature geometry)}
                                  {})}]]
         (cond
           point-search-loading?
           [:div.flex.items-center.justify-center.h-full.w-full.lg:w-1-3.lg:ml-2.pt-4
            [:div
             (cc/loading-spinner {:with-overlay false})]]

           selected-result
           [selected-result-view selected-result]

           selected-cluster-results
           [selected-cluster-view selected-cluster-results]

           selected-property
           [selected-property-view selected-property])]))))

(defn- map-view [_tr]
  [complaint-search-result-map])

(defn view
  ([]
   (view nil))
  ([{:keys [include-secondary-map-records? combined-map-available? on-toggle-secondary-map-records]}]
   (let [tr              (<sub [:tr])
         landing-config  (<sub [::landing-page/landing-config])
         visible-filters (<sub [::visible-filters])
         n-results       (<sub [::total-count])
         in-progress?    (<sub [:comm/command-processing? :public-records/search-complaints])]
     [:div.flex.flex-col.gap-4
      (when (and (:show-complaints? landing-config)
                 (:complaint-submission? landing-config))
        [:div (cc/button {:size     :small
                          :on-click #(>evt [:route/push {:route :view/create-complaint
                                                         :query {:origin "public-records"}}])}
                         (tr [:public-records/submit-complaint]))])
      [pr-common/address-pin-search tr [::selected-or-all-municipality-numbers] ::select-property]
      [pr-common/selected-property-chip tr [::selected-property] ::clear-selected-property]
      [search-filter tr]
      [prui/result-count+switcher tr n-results in-progress?
       :class-name            (if (seq visible-filters) "mt-8 mb-4" "mb-4")
       :selected-view-sub     ::selected-view
       :set-selected-view-evt ::set-selected-view]
      (case (<sub [::selected-view])
        :dashboard-search/list-view
        [list-view tr in-progress?]

        [:div
         (when combined-map-available?
           [:div.flex.justify-end.items-center.mb-4
            [:span.mr-3 (tr [:public-records/include-cases])]
            (cc/toggle {:id         "public-records-add-cases-toggle"
                        :size       :sm
                        :class-name "unlabeled-toggle"
                        :labelA     (tr [:false])
                        :labelB     (tr [:true])
                        :aria-label (tr [:public-records/include-cases])
                        :toggled    include-secondary-map-records?
                        :on-toggle  on-toggle-secondary-map-records})])
         [map-view tr]])])))
